<template>
  <b-modal id="subjectsettingswin" title="Настройки предмета" ok-title="Сохранить" cancel-title="Отмена"
    :ok-disabled="!canSave" :busy="saving" @show="onShow" @ok.prevent="save">
    <div class="ss-subject">
      <div class="ss-predm">{{ ctx.predm }}</div>
      <div class="ss-groups">{{ (ctx.groups || []).join(', ') }}</div>
      <div class="ss-note">Настройки действуют для всех занятий этого предмета у этой группы (групп).</div>
    </div>

    <b-overlay :show="loading" rounded="sm">
      <!-- свой переключатель: switch из bootstrap-vue рассчитан на Bootstrap 4 и
           в Bootstrap 5 проекта рисуется обычным флажком -->
      <label class="ss-toggle">
        <input v-model="form.load_subgroups" type="checkbox" role="switch" class="ss-toggle-input">
        <span class="ss-track" aria-hidden="true"><span class="ss-thumb"></span></span>
        <span class="ss-toggle-text">Загружать подгруппы</span>
      </label>
      <p class="ss-help">
        При каждой загрузке расписания с ВятГУ подгруппы берутся из расписания кафедры
        («Занятость преподавателей» на vyatsu.ru) и подставляются при записи занятий в журнал ISS —
        выбирать подгруппу вручную не придётся.
      </p>

      <template v-if="form.load_subgroups">
        <label class="ss-label" for="ss-kaf">Кафедра</label>
        <b-form-select id="ss-kaf" v-model="form.kaf_id" :options="kafOptions" class="mb-1" @change="kafChanged">
          <template #first><b-form-select-option :value="''" disabled>Выберите кафедру</b-form-select-option></template>
        </b-form-select>
        <div v-if="period" class="ss-period">Расписание кафедры с {{ fmt(period.from) }} по {{ fmt(period.to) }}</div>

        <template v-if="form.kaf_id">
          <label class="ss-label mt-2" for="ss-teacher">Преподаватель</label>
          <b-form-select id="ss-teacher" v-model="form.teacher" :options="teachers" :disabled="teachersLoading || !teachers.length">
            <template #first><b-form-select-option :value="''" disabled>{{ teachersLoading ? 'Загрузка…' : 'Выберите себя в списке' }}</b-form-select-option></template>
          </b-form-select>
          <b-alert v-if="kafSelectedNoFile" show variant="warning" class="mt-2 mb-0">
            У кафедры нет расписания на текущую дату — подгруппы подгрузятся, когда оно появится.
          </b-alert>
        </template>
      </template>

      <b-alert v-if="error" show variant="danger" class="mt-3 mb-0">{{ error }}</b-alert>
    </b-overlay>
  </b-modal>
</template>

<script>
const PATH = process.env.VUE_APP_PATH

export default {
  name: 'subjectSettingsWin',
  props: {
    login: String,
    passwordAES: String,
    // {predm, groups: [...]}
    ctx: Object,
  },
  data() {
    return {
      form: { load_subgroups: false, kaf_id: '', teacher: '' },
      departments: [],
      teachers: [],
      period: null,
      loading: false,
      teachersLoading: false,
      kafSelectedNoFile: false,
      saving: false,
      error: '',
      depsPromise: null,
    }
  },
  computed: {
    auth() {
      return { login: this.login, passwordAES: this.passwordAES }
    },
    kafOptions() {
      // кафедры, сгруппированные по институтам
      const byFaculty = new Map()
      this.departments.forEach(d => {
        if (!byFaculty.has(d.faculty)) byFaculty.set(d.faculty, [])
        byFaculty.get(d.faculty).push({ value: d.id, text: d.name.replace(/\s*\(ОРУ\)$/, '') })
      })
      return [...byFaculty].map(([label, options]) => ({ label: label.replace(/\s*\(ОРУ\)$/, ''), options }))
    },
    canSave() {
      if (this.loading || this.saving) return false
      return !this.form.load_subgroups || (!!this.form.kaf_id && !!this.form.teacher)
    },
  },
  watch: {
    'form.load_subgroups'(on) {
      if (on && !this.departments.length) this.loadDepartments()
    },
  },
  methods: {
    fmt(day) {
      return day ? day.split('-').reverse().slice(0, 2).join('.') : ''
    },
    errText(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.error) || fallback
    },
    async onShow() {
      this.error = ''
      this.period = null
      this.teachers = []
      this.kafSelectedNoFile = false
      this.form = { load_subgroups: false, kaf_id: '', teacher: '' }
      this.loading = true
      try {
        const { data } = await this.axios.post(PATH + '/api/subgroups/settings/get', { ...this.auth, predm: this.ctx.predm, groups: this.ctx.groups })
        this.form = { load_subgroups: !!data.load_subgroups, kaf_id: data.kaf_id || '', teacher: data.teacher || '' }
        if (this.form.load_subgroups) {
          await this.loadDepartments()
          if (this.form.kaf_id) await this.loadTeachers(true)
        }
      } catch (e) {
        this.error = this.errText(e, 'Не удалось загрузить настройки')
      } finally {
        this.loading = false
      }
    },
    // один запрос на открытие окна, даже если его одновременно запросили
    // onShow и наблюдатель переключателя
    loadDepartments() {
      if (this.departments.length) return Promise.resolve()
      if (!this.depsPromise) {
        this.loading = true
        this.depsPromise = this.axios.post(PATH + '/api/subgroups/departments', this.auth)
          .then(({ data }) => { this.departments = data })
          .catch(e => { this.error = this.errText(e, 'Не удалось загрузить список кафедр с сайта ВятГУ') })
          .finally(() => { this.loading = false; this.depsPromise = null })
      }
      return this.depsPromise
    },
    kafChanged() {
      this.form.teacher = ''
      this.loadTeachers(false)
    },
    async loadTeachers(keepTeacher) {
      this.teachers = []
      this.period = null
      this.kafSelectedNoFile = false
      if (!this.form.kaf_id) return
      this.teachersLoading = true
      this.error = ''
      try {
        const { data } = await this.axios.post(PATH + '/api/subgroups/teachers', { ...this.auth, kaf_id: this.form.kaf_id })
        this.teachers = data.teachers
        this.period = data.file
        this.kafSelectedNoFile = !data.file
        // сохранённый преподаватель мог пропасть из текущего файла — оставим его в списке
        if (keepTeacher && this.form.teacher && !this.teachers.includes(this.form.teacher)) this.teachers.unshift(this.form.teacher)
      } catch (e) {
        this.error = this.errText(e, 'Не удалось загрузить расписание кафедры')
      } finally {
        this.teachersLoading = false
      }
    },
    async save() {
      this.saving = true
      this.error = ''
      try {
        const { data } = await this.axios.post(PATH + '/api/subgroups/settings/save', {
          ...this.auth,
          predm: this.ctx.predm,
          groups: this.ctx.groups,
          load_subgroups: this.form.load_subgroups,
          kaf_id: this.form.kaf_id,
          teacher: this.form.teacher,
        })
        let msg
        if (this.form.load_subgroups) {
          msg = `Подгруппы: найдено занятий в расписании кафедры — ${data.matched}, обновлено — ${data.updated}.`
          if (data.errors && data.errors.length) msg += ' ' + data.errors.join('; ')
        } else {
          msg = data.cleared ? `Подгрузка подгрупп выключена, данные о подгруппах удалены у ${data.cleared} занятий.` : 'Подгрузка подгрупп выключена.'
        }
        this.$bvToast.toast(msg, { title: 'Настройки предмета сохранены', variant: data.errors && data.errors.length ? 'warning' : 'success', solid: true, autoHideDelay: 7000 })
        this.$emit('saved')
        this.$bvModal.hide('subjectsettingswin')
      } catch (e) {
        this.error = this.errText(e, 'Не удалось сохранить настройки')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.ss-subject {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);
}
.ss-predm {
  font-weight: 600;
  font-size: 1.05rem;
}
.ss-groups {
  color: var(--primary);
  font-size: .9rem;
}
.ss-note, .ss-help, .ss-period {
  color: var(--text-muted);
  font-size: .82rem;
}
.ss-note {
  margin-top: 4px;
}
.ss-toggle {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  margin-bottom: 2px;
  cursor: pointer;
  user-select: none;
}
.ss-toggle-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}
.ss-track {
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background-color: var(--border);
  transition: background-color .2s ease;
}
.ss-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px var(--shadow);
  transition: transform .2s ease;
}
.ss-toggle-input:checked + .ss-track {
  background-color: var(--primary);
}
.ss-toggle-input:checked + .ss-track .ss-thumb {
  transform: translateX(20px);
}
.ss-toggle-input:focus-visible + .ss-track {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.ss-toggle-text {
  font-weight: 600;
}
@media (prefers-reduced-motion: reduce) {
  .ss-track, .ss-thumb {
    transition: none;
  }
}
.ss-help {
  margin-bottom: 12px;
}
.ss-label {
  display: block;
  font-size: .85rem;
  font-weight: 600;
  margin-bottom: 4px;
}
</style>
